// Single source of truth for the app's design tokens.
//
// A "theme" is one plain-JSON object (see THEME_DEFAULTS). It is persisted with the
// rest of the user preferences (`prefs.appearance`), turned into CSS custom
// properties by AppearanceContext, into an MUI theme by buildTheme.js and into an
// xterm.js option set by `terminalOptionsFrom`. Nothing else may hardcode a color,
// a font or a radius — add a token here instead.

export const FONT_STACKS = {
  "JetBrains Mono": '"JetBrains Mono", "Consolas", monospace',
  Oxanium: '"Oxanium", "Segoe UI", sans-serif',
  Inter: '"Inter", "Segoe UI", sans-serif',
  "Space Grotesk": '"Space Grotesk", "Segoe UI", sans-serif',
  Rajdhani: '"Rajdhani", "Segoe UI", sans-serif',
  Orbitron: '"Orbitron", "Segoe UI", sans-serif',
  System: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  "Fira Code": '"Fira Code", "JetBrains Mono", monospace',
  "IBM Plex Mono": '"IBM Plex Mono", "JetBrains Mono", monospace',
  "Source Code Pro": '"Source Code Pro", "JetBrains Mono", monospace',
  "Share Tech Mono": '"Share Tech Mono", "JetBrains Mono", monospace',
  "Space Mono": '"Space Mono", "JetBrains Mono", monospace',
  "System Mono": 'ui-monospace, SFMono-Regular, "Consolas", monospace',
}

export const UI_FONTS = ["JetBrains Mono", "Oxanium", "Inter", "Space Grotesk", "Rajdhani", "Orbitron", "System"]
export const MONO_FONTS = ["JetBrains Mono", "Fira Code", "IBM Plex Mono", "Source Code Pro", "Share Tech Mono", "Space Mono", "System Mono"]

export const fontStack = (name) => FONT_STACKS[name] ?? FONT_STACKS["JetBrains Mono"]

export const ANSI_KEYS = [
  "black", "red", "green", "yellow", "blue", "magenta", "cyan", "white",
  "brightBlack", "brightRed", "brightGreen", "brightYellow", "brightBlue", "brightMagenta", "brightCyan", "brightWhite",
]

export const COLOR_FIELDS = [
  { key: "accent", label: "Akzent", hint: "Primärfarbe für Buttons, Ränder, Fokus" },
  { key: "accentAlt", label: "Akzent 2", hint: "Sekundärfarbe für Verläufe und Badges" },
  { key: "background", label: "Hintergrund", hint: "App-Hintergrund" },
  { key: "paper", label: "Fläche", hint: "Panels, Karten, Dialoge" },
  { key: "elevated", label: "Erhöht", hint: "Menüs, Popover, Taskleiste" },
  { key: "border", label: "Rand", hint: "Trennlinien und Rahmen" },
  { key: "text", label: "Text", hint: "Primärer Text" },
  { key: "textDim", label: "Text gedimmt", hint: "Sekundärer Text und Labels" },
  { key: "success", label: "Erfolg" },
  { key: "warning", label: "Warnung" },
  { key: "error", label: "Fehler" },
  { key: "info", label: "Info" },
  { key: "jarvis", label: "JARVIS", hint: "Identitätsfarbe des JARVIS-Agenten" },
  { key: "friday", label: "FRIDAY", hint: "Identitätsfarbe des FRIDAY-Agenten" },
]

// Per-Bereich-Identitätsfarben (NavRail, Seiten-Header, Karten-Akzente).
export const FEATURE_FIELDS = [
  { key: "vault", label: "Vault" },
  { key: "research", label: "Research" },
  { key: "hermes", label: "Hermes" },
  { key: "chat", label: "Messenger" },
  { key: "stats", label: "Server" },
  { key: "database", label: "Datenbank" },
  { key: "mail", label: "Mail" },
  { key: "ide", label: "IDE" },
  { key: "desktop", label: "Desktop" },
  { key: "chill", label: "Chill" },
  { key: "reminder", label: "Reminder" },
  { key: "hut", label: "Hut & Heroes" },
  { key: "analysis", label: "Analyse" },
  { key: "tests", label: "Tests" },
]

export const DENSITIES = [
  { value: "compact", label: "Kompakt", gap: 6, padY: 6, spacing: 7 },
  { value: "comfortable", label: "Komfortabel", gap: 12, padY: 10, spacing: 8 },
  { value: "spacious", label: "Weit", gap: 18, padY: 14, spacing: 9 },
]

export const CURSOR_STYLES = ["block", "bar", "underline"]

const ANSI_DEFAULT = {
  black: "#1e293b", red: "#ef4444", green: "#22c55e", yellow: "#eab308",
  blue: "#00b4ff", magenta: "#a855f7", cyan: "#06b6d4", white: "#e2e8f0",
  brightBlack: "#475569", brightRed: "#f87171", brightGreen: "#4ade80", brightYellow: "#fbbf24",
  brightBlue: "#38bdf8", brightMagenta: "#c084fc", brightCyan: "#22d3ee", brightWhite: "#f8fafc",
}

export const THEME_DEFAULTS = {
  preset: "holos",
  mode: "dark",
  colors: {
    accent: "#00b4ff", accentAlt: "#a855f7",
    background: "#08080f", paper: "#0d0d1a", elevated: "#12121f",
    border: "#ffffff", text: "#e2e8f0", textDim: "#64748b",
    success: "#22c55e", warning: "#f59e0b", error: "#ef4444", info: "#38bdf8",
    jarvis: "#00b4ff", friday: "#ff6b00",
  },
  features: {
    vault: "#a855f7", research: "#14b8a6", hermes: "#ffb020", chat: "#f59e0b",
    stats: "#22d3ee", database: "#10b981", mail: "#f43f5e", ide: "#38bdf8",
    desktop: "#38bdf8", chill: "#c084fc", reminder: "#f472b6", hut: "#facc15",
    analysis: "#818cf8", tests: "#2dd4bf",
  },
  typography: { ui: "JetBrains Mono", mono: "JetBrains Mono", size: 14, letterSpacing: 0 },
  shape: { radius: 6, density: "comfortable", borderOpacity: 0.08, surfaceOpacity: 1 },
  effects: { glass: true, blur: 12, glow: 0.35, reduceAnimations: false },
  terminal: {
    follow: true,
    background: "#08080f", foreground: "#e2e8f0", cursor: "#00b4ff", selection: "#00b4ff",
    ansi: { ...ANSI_DEFAULT },
    font: "JetBrains Mono", fontSize: 13, lineHeight: 1.2,
    cursorStyle: "block", cursorBlink: true,
  },
}

// Presets are partial token trees deep-merged onto THEME_DEFAULTS.
export const THEME_PRESETS = {
  holos: { label: "Holos", colors: {} },
  ember: {
    label: "Ember",
    colors: { accent: "#ff6b00", accentAlt: "#f43f5e", background: "#0c0705", paper: "#1a0d08", elevated: "#241109", info: "#fb923c" },
    terminal: { ansi: { blue: "#ff6b00", brightBlue: "#fb923c", cyan: "#f59e0b", brightCyan: "#fbbf24" } },
  },
  nord: {
    label: "Nord",
    colors: { accent: "#88c0d0", accentAlt: "#b48ead", background: "#2e3440", paper: "#3b4252", elevated: "#434c5e", text: "#eceff4", textDim: "#8b93a5", success: "#a3be8c", warning: "#ebcb8b", error: "#bf616a", info: "#81a1c1", jarvis: "#88c0d0", friday: "#d08770" },
    terminal: { ansi: { black: "#3b4252", red: "#bf616a", green: "#a3be8c", yellow: "#ebcb8b", blue: "#81a1c1", magenta: "#b48ead", cyan: "#88c0d0", white: "#e5e9f0", brightBlack: "#4c566a", brightRed: "#bf616a", brightGreen: "#a3be8c", brightYellow: "#ebcb8b", brightBlue: "#81a1c1", brightMagenta: "#b48ead", brightCyan: "#8fbcbb", brightWhite: "#eceff4" } },
  },
  dracula: {
    label: "Dracula",
    colors: { accent: "#bd93f9", accentAlt: "#ff79c6", background: "#282a36", paper: "#21222c", elevated: "#343746", text: "#f8f8f2", textDim: "#6272a4", success: "#50fa7b", warning: "#ffb86c", error: "#ff5555", info: "#8be9fd", jarvis: "#8be9fd", friday: "#ffb86c" },
    terminal: { ansi: { black: "#21222c", red: "#ff5555", green: "#50fa7b", yellow: "#f1fa8c", blue: "#bd93f9", magenta: "#ff79c6", cyan: "#8be9fd", white: "#f8f8f2", brightBlack: "#6272a4", brightRed: "#ff6e6e", brightGreen: "#69ff94", brightYellow: "#ffffa5", brightBlue: "#d6acff", brightMagenta: "#ff92df", brightCyan: "#a4ffff", brightWhite: "#ffffff" } },
  },
  matrix: {
    label: "Matrix",
    colors: { accent: "#22c55e", accentAlt: "#84cc16", background: "#000000", paper: "#050d07", elevated: "#0a1a0d", text: "#c8facc", textDim: "#4d8757", info: "#4ade80", jarvis: "#22c55e", friday: "#84cc16" },
    typography: { ui: "Share Tech Mono", mono: "Share Tech Mono" },
    features: { vault: "#84cc16", research: "#4ade80", hermes: "#a3e635", chat: "#22c55e", stats: "#86efac", database: "#22c55e", mail: "#84cc16", ide: "#4ade80", desktop: "#4ade80", chill: "#a3e635", reminder: "#86efac", hut: "#bef264", analysis: "#4ade80", tests: "#86efac" },
    terminal: { ansi: { blue: "#22c55e", magenta: "#84cc16", cyan: "#4ade80", white: "#c8facc", brightBlue: "#4ade80", brightMagenta: "#a3e635", brightCyan: "#86efac", brightWhite: "#dcfce7" } },
  },
  synthwave: {
    label: "Synthwave",
    colors: { accent: "#f92aad", accentAlt: "#03edf9", background: "#150c22", paper: "#1e142e", elevated: "#2a1b3d", text: "#f4eefc", textDim: "#8f7aa8", success: "#72f1b8", warning: "#fede5d", error: "#fe4450", info: "#03edf9", jarvis: "#03edf9", friday: "#ff8b39" },
    typography: { ui: "Orbitron" },
    effects: { glow: 0.7 },
    features: { vault: "#f92aad", research: "#03edf9", hermes: "#fede5d", chat: "#ff8b39", stats: "#36f9f6", database: "#72f1b8", mail: "#fe4450", ide: "#03edf9", desktop: "#ff7edb", chill: "#ff7edb", reminder: "#f92aad", hut: "#fede5d", analysis: "#ff7edb", tests: "#72f1b8" },
    terminal: { ansi: { black: "#2a1b3d", red: "#fe4450", green: "#72f1b8", yellow: "#fede5d", blue: "#03edf9", magenta: "#f92aad", cyan: "#03edf9", white: "#f4eefc", brightBlack: "#495495", brightRed: "#fe4450", brightGreen: "#72f1b8", brightYellow: "#fede5d", brightBlue: "#36f9f6", brightMagenta: "#ff7edb", brightCyan: "#36f9f6", brightWhite: "#ffffff" } },
  },
  daybreak: {
    label: "Daybreak (hell)",
    mode: "light",
    colors: { accent: "#0369a1", accentAlt: "#7c3aed", background: "#f6f7fb", paper: "#ffffff", elevated: "#eef1f7", border: "#0f172a", text: "#0f172a", textDim: "#5b6478", success: "#15803d", warning: "#b45309", error: "#b91c1c", info: "#0369a1", jarvis: "#0369a1", friday: "#c2410c" },
    typography: { ui: "Inter" },
    shape: { borderOpacity: 0.14 },
    features: { vault: "#7c3aed", research: "#0f766e", hermes: "#b45309", chat: "#b45309", stats: "#0e7490", database: "#047857", mail: "#be123c", ide: "#0369a1", desktop: "#0369a1", chill: "#7c3aed", reminder: "#be185d", hut: "#a16207", analysis: "#4f46e5", tests: "#0f766e" },
    effects: { glow: 0.12 },
    terminal: { background: "#ffffff", foreground: "#0f172a", ansi: { black: "#1e293b", red: "#b91c1c", green: "#15803d", yellow: "#b45309", blue: "#0369a1", magenta: "#7c3aed", cyan: "#0e7490", white: "#475569", brightBlack: "#64748b", brightRed: "#dc2626", brightGreen: "#16a34a", brightYellow: "#d97706", brightBlue: "#0284c7", brightMagenta: "#9333ea", brightCyan: "#0891b2", brightWhite: "#0f172a" } },
  },
  sandstone: {
    label: "Sandstone (hell)",
    mode: "light",
    colors: { accent: "#b45309", accentAlt: "#0f766e", background: "#f5f1e8", paper: "#fdfaf3", elevated: "#ece5d5", border: "#3f3a2f", text: "#2b2620", textDim: "#6b6152", success: "#4d7c0f", warning: "#b45309", error: "#b91c1c", info: "#0f766e", jarvis: "#0f766e", friday: "#b45309" },
    typography: { ui: "Space Grotesk" },
    shape: { radius: 10, borderOpacity: 0.16 },
    features: { vault: "#7c3aed", research: "#0f766e", hermes: "#b45309", chat: "#a16207", stats: "#0e7490", database: "#4d7c0f", mail: "#be123c", ide: "#0f766e", desktop: "#0f766e", chill: "#9333ea", reminder: "#be185d", hut: "#a16207", analysis: "#4f46e5", tests: "#0f766e" },
    effects: { glow: 0.1 },
    terminal: { background: "#fdfaf3", foreground: "#2b2620", ansi: { black: "#3f3a2f", red: "#b91c1c", green: "#4d7c0f", yellow: "#b45309", blue: "#0f766e", magenta: "#9333ea", cyan: "#0e7490", white: "#6b6152", brightBlack: "#8a7f6c", brightRed: "#dc2626", brightGreen: "#65a30d", brightYellow: "#d97706", brightBlue: "#0d9488", brightMagenta: "#a855f7", brightCyan: "#0891b2", brightWhite: "#2b2620" } },
  },
  mono: {
    label: "Monochrome",
    colors: { accent: "#e2e8f0", accentAlt: "#94a3b8", background: "#0a0a0a", paper: "#121212", elevated: "#1c1c1c", text: "#f4f4f5", textDim: "#71717a", success: "#a1a1aa", warning: "#d4d4d8", error: "#fafafa", info: "#a1a1aa", jarvis: "#e4e4e7", friday: "#a1a1aa" },
    typography: { ui: "IBM Plex Mono", mono: "IBM Plex Mono" },
    shape: { radius: 2 },
    features: { vault: "#d4d4d8", research: "#a1a1aa", hermes: "#e4e4e7", chat: "#d4d4d8", stats: "#a1a1aa", database: "#a1a1aa", mail: "#e4e4e7", ide: "#d4d4d8", desktop: "#d4d4d8", chill: "#a1a1aa", reminder: "#e4e4e7", hut: "#f4f4f5", analysis: "#a1a1aa", tests: "#d4d4d8" },
    effects: { glass: false, glow: 0 },
    terminal: { ansi: { black: "#27272a", red: "#d4d4d8", green: "#a1a1aa", yellow: "#e4e4e7", blue: "#a1a1aa", magenta: "#d4d4d8", cyan: "#e4e4e7", white: "#f4f4f5", brightBlack: "#52525b", brightRed: "#fafafa", brightGreen: "#d4d4d8", brightYellow: "#fafafa", brightBlue: "#d4d4d8", brightMagenta: "#fafafa", brightCyan: "#fafafa", brightWhite: "#ffffff" } },
  },
}

export const PRESET_IDS = Object.keys(THEME_PRESETS)

// ── validation helpers ────────────────────────────────────────────────────────

const HEX_RE = /^#[0-9a-f]{6}$/i

export const isHex = (value) => typeof value === "string" && HEX_RE.test(value)

const hex = (value, fallback) => (isHex(value) ? value.toLowerCase() : fallback)
const num = (value, min, max, fallback) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback
}
const pick = (value, options, fallback) => (options.includes(value) ? value : fallback)
const bool = (value, fallback) => (typeof value === "boolean" ? value : fallback)

export function hexToRgb(value) {
  const safe = isHex(value) ? value : "#000000"
  return [1, 3, 5].map((offset) => parseInt(safe.slice(offset, offset + 2), 16))
}

export function withAlpha(value, alpha) {
  const [r, g, b] = hexToRgb(value)
  return `rgba(${r}, ${g}, ${b}, ${Math.round(alpha * 1000) / 1000})`
}

// Relative luminance — used to pick readable on-accent text and cursor contrast.
export function luminance(value) {
  const channels = hexToRgb(value).map((raw) => {
    const c = raw / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

export const contrastText = (value) => (luminance(value) > 0.45 ? "#0b0b12" : "#ffffff")

// SVG presentation attributes and canvas contexts cannot parse `var()`. Renderers
// that paint instead of styling resolve the token to a concrete color first.
export function resolveCssColor(value, fallback = "#94a3b8") {
  if (typeof value !== "string") return fallback
  const name = value.match(/^var\((--[\w-]+)\)$/)?.[1]
  if (!name) return value
  if (typeof document === "undefined") return fallback
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
}

export function mixHex(from, to, ratio) {
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  const parts = a.map((channel, index) => Math.round(channel + (b[index] - channel) * ratio))
  return `#${parts.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`
}

function deepMerge(base, patch) {
  if (!patch || typeof patch !== "object") return base
  const out = { ...base }
  Object.entries(patch).forEach(([key, value]) => {
    out[key] = value && typeof value === "object" && !Array.isArray(value) && base[key] && typeof base[key] === "object"
      ? deepMerge(base[key], value)
      : value
  })
  return out
}

// ── normalization ─────────────────────────────────────────────────────────────

// Accepts the legacy flat shape `{ accent, density, reduceAnimations }` as well as
// the full token tree, so preferences saved before the theme engine still load.
export function normalizeTheme(value = {}) {
  const source = value && typeof value === "object" ? value : {}
  const legacy = {
    colors: isHex(source.accent) ? { accent: source.accent } : {},
    shape: typeof source.density === "string" ? { density: source.density } : {},
    effects: typeof source.reduceAnimations === "boolean" ? { reduceAnimations: source.reduceAnimations } : {},
  }
  const merged = deepMerge(deepMerge(THEME_DEFAULTS, legacy), source)
  const defaults = THEME_DEFAULTS
  const colors = {}
  COLOR_FIELDS.forEach(({ key }) => { colors[key] = hex(merged.colors?.[key], defaults.colors[key]) })
  const features = {}
  FEATURE_FIELDS.forEach(({ key }) => { features[key] = hex(merged.features?.[key], defaults.features[key]) })
  const ansi = {}
  ANSI_KEYS.forEach((key) => { ansi[key] = hex(merged.terminal?.ansi?.[key], defaults.terminal.ansi[key]) })

  return {
    preset: pick(merged.preset, [...PRESET_IDS, "custom"], defaults.preset),
    mode: pick(merged.mode, ["dark", "light"], defaults.mode),
    colors,
    features,
    typography: {
      ui: pick(merged.typography?.ui, UI_FONTS, defaults.typography.ui),
      mono: pick(merged.typography?.mono, MONO_FONTS, defaults.typography.mono),
      size: num(merged.typography?.size, 11, 20, defaults.typography.size),
      letterSpacing: num(merged.typography?.letterSpacing, -0.05, 0.2, defaults.typography.letterSpacing),
    },
    shape: {
      radius: num(merged.shape?.radius, 0, 24, defaults.shape.radius),
      density: pick(merged.shape?.density, DENSITIES.map((item) => item.value), defaults.shape.density),
      borderOpacity: num(merged.shape?.borderOpacity, 0, 0.6, defaults.shape.borderOpacity),
      surfaceOpacity: num(merged.shape?.surfaceOpacity, 0.4, 1, defaults.shape.surfaceOpacity),
    },
    effects: {
      glass: bool(merged.effects?.glass, defaults.effects.glass),
      blur: num(merged.effects?.blur, 0, 40, defaults.effects.blur),
      glow: num(merged.effects?.glow, 0, 1, defaults.effects.glow),
      reduceAnimations: bool(merged.effects?.reduceAnimations, defaults.effects.reduceAnimations),
    },
    terminal: {
      follow: bool(merged.terminal?.follow, defaults.terminal.follow),
      background: hex(merged.terminal?.background, defaults.terminal.background),
      foreground: hex(merged.terminal?.foreground, defaults.terminal.foreground),
      cursor: hex(merged.terminal?.cursor, defaults.terminal.cursor),
      selection: hex(merged.terminal?.selection, defaults.terminal.selection),
      ansi,
      font: pick(merged.terminal?.font, MONO_FONTS, defaults.terminal.font),
      fontSize: num(merged.terminal?.fontSize, 9, 24, defaults.terminal.fontSize),
      lineHeight: num(merged.terminal?.lineHeight, 1, 2, defaults.terminal.lineHeight),
      cursorStyle: pick(merged.terminal?.cursorStyle, CURSOR_STYLES, defaults.terminal.cursorStyle),
      cursorBlink: bool(merged.terminal?.cursorBlink, defaults.terminal.cursorBlink),
    },
  }
}

export function applyPreset(presetId) {
  const preset = THEME_PRESETS[presetId]
  if (!preset) return normalizeTheme({})
  const { label, ...tokens } = preset
  return normalizeTheme(deepMerge(deepMerge(THEME_DEFAULTS, tokens), { preset: presetId }))
}

export const densityOf = (tokens) => DENSITIES.find((item) => item.value === tokens.shape.density) ?? DENSITIES[1]

// ── derived outputs ───────────────────────────────────────────────────────────

// Terminal colors either follow the app palette (`follow: true`) or use their own.
export function resolveTerminalColors(tokens) {
  const { terminal, colors } = tokens
  if (!terminal.follow) return terminal
  return {
    ...terminal,
    background: colors.background,
    foreground: colors.text,
    cursor: colors.accent,
    selection: colors.accent,
  }
}

export function xtermThemeFrom(tokens) {
  const term = resolveTerminalColors(tokens)
  return {
    ...term.ansi,
    background: term.background,
    foreground: term.foreground,
    cursor: term.cursor,
    cursorAccent: term.background,
    selectionBackground: withAlpha(term.selection, 0.33),
    scrollbarSliderBackground: withAlpha(tokens.colors.accent, 0.18),
    scrollbarSliderHoverBackground: withAlpha(tokens.colors.accent, 0.35),
    scrollbarSliderActiveBackground: withAlpha(tokens.colors.accent, 0.55),
  }
}

export function terminalOptionsFrom(tokens) {
  const term = resolveTerminalColors(tokens)
  return {
    theme: xtermThemeFrom(tokens),
    fontFamily: fontStack(term.font),
    fontSize: term.fontSize,
    lineHeight: term.lineHeight,
    cursorStyle: term.cursorStyle,
    cursorBlink: term.cursorBlink,
  }
}

// Every CSS custom property the app may reference. Applied to <html> so that plain
// CSS, `sx` literals and canvas-free components all read the same values.
export function themeCssVars(tokens) {
  const { colors, features, typography, shape, effects } = tokens
  const density = densityOf(tokens)
  const term = resolveTerminalColors(tokens)
  const border = withAlpha(colors.border, shape.borderOpacity)
  return {
    "--app-accent": colors.accent,
    // RGB triplets for the `rgba(var(--…), α)` overlays used across the app: they
    // let every neutral tint follow the theme instead of assuming white-on-dark.
    "--app-fg-rgb": hexToRgb(colors.text).join(", "),
    "--app-bg-rgb": hexToRgb(colors.background).join(", "),
    "--app-accent-rgb": hexToRgb(colors.accent).join(", "),
    "--app-accent-alt": colors.accentAlt,
    "--app-accent-soft": withAlpha(colors.accent, 0.12),
    "--app-accent-softer": withAlpha(colors.accent, 0.06),
    "--app-accent-strong": withAlpha(colors.accent, 0.35),
    "--app-accent-contrast": contrastText(colors.accent),
    "--app-bg": colors.background,
    "--app-bg-alt": mixHex(colors.background, colors.paper, 0.5),
    "--app-paper": colors.paper,
    "--app-paper-translucent": withAlpha(colors.paper, shape.surfaceOpacity),
    "--app-elevated": colors.elevated,
    "--app-border": border,
    "--app-border-strong": withAlpha(colors.border, Math.min(0.6, shape.borderOpacity * 2.5)),
    "--app-text": colors.text,
    "--app-text-dim": colors.textDim,
    "--app-success": colors.success,
    "--app-warning": colors.warning,
    "--app-error": colors.error,
    "--app-info": colors.info,
    "--agent-jarvis": colors.jarvis,
    "--agent-friday": colors.friday,
    "--app-font-ui": fontStack(typography.ui),
    "--app-font-mono": fontStack(typography.mono),
    "--app-font-size": `${typography.size}px`,
    "--app-letter-spacing": `${typography.letterSpacing}em`,
    "--app-radius": `${shape.radius}px`,
    "--app-radius-lg": `${Math.round(shape.radius * 1.8)}px`,
    "--app-blur": effects.glass ? `${effects.blur}px` : "0px",
    "--app-glow": String(effects.glow),
    "--app-glow-accent": effects.glow > 0 ? `0 0 ${Math.round(10 + effects.glow * 26)}px ${withAlpha(colors.accent, effects.glow * 0.6)}` : "none",
    "--app-scrollbar-thumb": withAlpha(colors.accent, 0.28),
    "--app-scrollbar-thumb-hover": withAlpha(colors.accent, 0.5),
    // Shadows stay black on dark themes but soften to the text hue on light ones.
    "--app-shadow-rgb": tokens.mode === "light" ? hexToRgb(colors.textDim).join(", ") : "0, 0, 0",
    ...Object.fromEntries(Object.entries(features).map(([key, value]) => [`--feature-${key}`, value])),
    "--message-gap": `${density.gap}px`,
    "--message-padding-y": `${density.padY}px`,
    "--term-bg": term.background,
    "--term-fg": term.foreground,
    "--term-cursor": term.cursor,
    "--term-font": fontStack(term.font),
  }
}
